import React, { useState, useEffect, useRef, useCallback } from "react";
import { addItem } from "../api";

/**
 * Modal d'ajout d'article — nom, quantité, catégorie.
 * Sans scanner de code-barres.
 */
function AddItemForm({ categories, onAdd, onClose }) {
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [category, setCategory] = useState("Autre");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    setLoading(true);
    try {
      await addItem({ name: trimmed, quantity, category });
      onAdd();
    } catch (err) {
      console.error("Erreur lors de l'ajout:", err);
    } finally {
      setLoading(false);
    }
  };

  const isDirty = name.trim() !== "" || quantity !== "1" || category !== "Autre";

  const handleClose = useCallback(() => {
    if (isDirty && !window.confirm("Tu as des modifications non enregistrées. Fermer quand même ?")) {
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  // Fermer avec Escape + focus trap
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") { handleClose(); return; }
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'input, select, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleClose]);

  return (
    <div className="modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="add-item-title">
      <div className="modal-content" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title" id="add-item-title">Ajouter un article</h2>

        <form onSubmit={handleSubmit} className="add-form">
          <div className="form-group">
            <label htmlFor="item-name">Article</label>
            <input
              id="item-name"
              ref={inputRef}
              type="text"
              placeholder="Ex: Ciment, Vis, Peinture..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="item-quantity">Quantité</label>
              <input
                id="item-quantity"
                type="text"
                placeholder="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label htmlFor="item-category">Catégorie</label>
              <select
                id="item-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading || !name.trim()}>
              {loading ? "Ajout..." : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddItemForm;
